/**
 * V3 — Sage Écritures comptables multi-entités (CRUD complet).
 * Une écriture = date + journal + libellé + compte débit OU crédit (ou les deux) + montant.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { api } from './api';
import {
  Download, Search, ChevronLeft, ChevronRight, BookOpen, TrendingUp, TrendingDown,
  Plus, Pencil, Trash2, X, AlertTriangle, CheckCircle2,
} from 'lucide-react';

const baseURL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Math.round(Number(n || 0)).toLocaleString('fr-FR');

const ENTITY_COLORS = {
  CIS: '#22c55e', ROPS: '#f59e0b', NETBTP: '#ef4444',
  NKEYI: '#8b5cf6', R2S: '#fb923c', EXCUISINE: '#6366f1',
};
const ENTITIES = ['ROPS', 'NETBTP', 'CIS', 'NKEYI', 'R2S', 'EXCUISINE'];
const JOURNAUX = [
  { code: 'VTE', label: 'Ventes' },
  { code: 'ACH', label: 'Achats' },
  { code: 'BNQ', label: 'Banque' },
  { code: 'CAI', label: 'Caisse' },
  { code: 'OD',  label: 'Opérations diverses' },
];

const EMPTY_FORM = {
  entity: 'ROPS',
  date: new Date().toISOString().slice(0, 10),
  journal: 'VTE',
  piece: '',
  libelle: '',
  compteDebit: '',
  compteCredit: '',
  montant: '',
};

export default function V3Sage() {
  const [data, setData] = useState({
    items: [], total: 0, total_pages: 0,
    kpis: { total_debit: 0, total_credit: 0 },
    entities_available: [], journals_available: [],
  });
  const [entity, setEntity] = useState('ALL');
  const [journal, setJournal] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // CRUD
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_FORM, entity: entity === 'ALL' ? 'ROPS' : entity });
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (ec) => {
    setEditingId(ec.id);
    setFormData({
      entity: ec.entity || 'ROPS',
      date: ec.date || new Date().toISOString().slice(0, 10),
      journal: ec.journal || 'VTE',
      piece: ec.piece || '',
      libelle: ec.libelle || '',
      compteDebit: ec.compte_debit || '',
      compteCredit: ec.compte_credit || '',
      montant: ec.montant || '',
    });
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditingId(null); setFormError(null); };

  const submitForm = async (e) => {
    e?.preventDefault?.();
    if (!formData.libelle.trim()) { setFormError('Libellé obligatoire.'); return; }
    if (!formData.compteDebit.trim() && !formData.compteCredit.trim()) {
      setFormError('Au moins un compte (débit ou crédit).'); return;
    }
    const m = Number(formData.montant);
    if (!m || m <= 0) { setFormError('Montant doit être > 0.'); return; }
    setBusy(true); setFormError(null);
    try {
      const payload = {
        entity: formData.entity,
        date: formData.date,
        journal: formData.journal,
        piece: formData.piece.trim() || null,
        libelle: formData.libelle.trim(),
        compteDebit: formData.compteDebit.trim() || null,
        compteCredit: formData.compteCredit.trim() || null,
        montant: m,
      };
      if (editingId) {
        await api.put(`/api/v3/sage/${editingId}`, payload);
        showToast(`Écriture mise à jour (${fmt(m)} FCFA)`);
      } else {
        await api.post('/api/v3/sage', payload);
        showToast(`Écriture créée (${fmt(m)} FCFA)`);
      }
      closeForm();
      fetchData();
    } catch (e) {
      setFormError(e?.response?.data?.detail || e.message || 'Erreur réseau');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await api.delete(`/api/v3/sage/${confirmDelete.id}?entity=${encodeURIComponent(confirmDelete.entity)}`);
      showToast(`Écriture supprimée`);
      setConfirmDelete(null);
      fetchData();
    } catch (e) {
      showToast(e?.response?.data?.detail || e.message || 'Erreur', 'error');
    } finally {
      setBusy(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { entity, page, page_size: 30 };
      if (journal) params.journal = journal;
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      if (search) params.search = search;
      const { data: d } = await api.get('/api/v3/sage', { params });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [entity, journal, from, to, search, page]);

  useEffect(() => { Promise.resolve().then(fetchData); }, [fetchData]);

  const exportUrl = () => {
    const p = new URLSearchParams();
    p.set('entity', entity);
    if (from) p.set('from_date', from);
    if (to) p.set('to_date', to);
    return `${baseURL}/api/v3/sage/export.xlsx?${p}`;
  };

  return (
    <div className="v3-page" data-testid="v3-sage">
      <div className="v3-page-head">
        <h1>Comptabilité (Sage)</h1>
        <p>{data.total} écritures · {entity === 'ALL' ? 'toutes entités' : entity}</p>
      </div>

      <section className="v3-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <KpiCard icon={<BookOpen/>} label="Écritures" value={data.total} accent="cyan" />
        <KpiCard icon={<TrendingUp/>} label="Total Débit" value={fmt(data.kpis.total_debit) + ' FCFA'} accent="amber" />
        <KpiCard icon={<TrendingDown/>} label="Total Crédit" value={fmt(data.kpis.total_credit) + ' FCFA'} accent="emerald" />
      </section>

      <section className="v3-filters">
        <div className="v3-filter-grp">
          <label>Entité</label>
          <select value={entity} onChange={(e) => { setPage(1); setEntity(e.target.value); }} data-testid="filter-entity">
            <option value="ALL">Toutes</option>
            {data.entities_available.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="v3-filter-grp">
          <label>Journal</label>
          <select value={journal} onChange={(e) => { setPage(1); setJournal(e.target.value); }} data-testid="filter-journal">
            <option value="">Tous</option>
            {JOURNAUX.map(j => <option key={j.code} value={j.code}>{j.code} — {j.label}</option>)}
          </select>
        </div>
        <div className="v3-filter-grp">
          <label>Du</label>
          <input type="date" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} />
        </div>
        <div className="v3-filter-grp">
          <label>Au</label>
          <input type="date" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} />
        </div>
        <div className="v3-filter-grp v3-filter-search">
          <label>Recherche</label>
          <div className="v3-search-input">
            <Search size={14}/>
            <input type="text" placeholder="Libellé, pièce, compte…" value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }} />
          </div>
        </div>
        <button type="button" onClick={openCreate} className="v3-btn v3-btn-primary" data-testid="btn-new-sage">
          <Plus size={16}/>Nouvelle écriture
        </button>
        <a href={exportUrl()} className="v3-btn v3-btn-secondary" data-testid="btn-export-sage">
          <Download size={16}/>Excel
        </a>
      </section>

      <section className="v3-card v3-card-table">
        <div className="v3-table-wrap">
          <table className="v3-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Entité</th>
                <th>Journal</th>
                <th>Pièce</th>
                <th>Libellé</th>
                <th>Cpt Débit</th>
                <th>Cpt Crédit</th>
                <th className="num">Montant</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.id} className="v3-row" data-testid={`sage-row-${it.id}`}>
                  <td className="mono">{it.date}</td>
                  <td><span className="v3-entity-pill" style={{ background: (ENTITY_COLORS[it.entity] || '#94a3b8') + '20', color: ENTITY_COLORS[it.entity] || '#94a3b8' }}>{it.entity}</span></td>
                  <td><span className="v3-pill v3-pill-cat">{it.journal}</span></td>
                  <td className="mono">{it.piece || '—'}</td>
                  <td>{it.libelle}</td>
                  <td className="mono">{it.compte_debit || '—'}</td>
                  <td className="mono">{it.compte_credit || '—'}</td>
                  <td className="num strong">{fmt(it.montant)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {it.auto_generated ? (
                      <span title="Générée auto par l'app legacy (non modifiable)" style={{ color: '#71717a', fontSize: 11, padding: '2px 6px', background: 'rgba(255,255,255,0.05)', borderRadius: 4 }}>auto</span>
                    ) : (
                      <>
                        <button onClick={() => openEdit(it)} title="Modifier" aria-label="Modifier"
                          data-testid={`edit-sage-${it.id}`} className="v3-icon-btn" style={{ marginRight: 4 }}>
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => setConfirmDelete({
                          id: it.id, entity: it.entity, libelle: it.libelle, montant: it.montant,
                        })} title="Supprimer" aria-label="Supprimer"
                        data-testid={`delete-sage-${it.id}`} className="v3-icon-btn v3-icon-btn-danger">
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && <tr><td colSpan={9} className="v3-empty">Aucune écriture.</td></tr>}
              {loading && <tr><td colSpan={9} className="v3-empty">Chargement…</td></tr>}
            </tbody>
          </table>
        </div>
        {data.total_pages > 1 && (
          <div className="v3-pagination">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1}><ChevronLeft size={16}/>Préc.</button>
            <span>Page {page} / {data.total_pages}</span>
            <button onClick={() => setPage(p => Math.min(data.total_pages, p+1))} disabled={page >= data.total_pages}>Suiv.<ChevronRight size={16}/></button>
          </div>
        )}
      </section>

      {showForm && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeForm(); }}>
          <form className="v3-modal" onSubmit={submitForm} data-testid="sage-form">
            <div className="v3-modal-head">
              <h2>{editingId ? "Modifier l'écriture" : 'Nouvelle écriture comptable'}</h2>
              <button type="button" onClick={closeForm} aria-label="Fermer" className="v3-icon-btn"><X size={16} /></button>
            </div>
            <div className="v3-modal-body">
              <div className="v3-form-row">
                <FormField label="Entité *">
                  <select required value={formData.entity}
                    onChange={(e) => setFormData({ ...formData, entity: e.target.value })}
                    disabled={!!editingId} data-testid="sage-form-entity">
                    {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </FormField>
                <FormField label="Date *">
                  <input type="date" required value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="sage-form-date" />
                </FormField>
              </div>
              <div className="v3-form-row">
                <FormField label="Journal *">
                  <select required value={formData.journal}
                    onChange={(e) => setFormData({ ...formData, journal: e.target.value })}
                    data-testid="sage-form-journal">
                    {JOURNAUX.map(j => <option key={j.code} value={j.code}>{j.code} — {j.label}</option>)}
                  </select>
                </FormField>
                <FormField label="Pièce">
                  <input type="text" value={formData.piece}
                    onChange={(e) => setFormData({ ...formData, piece: e.target.value })}
                    placeholder="FAC-001" data-testid="sage-form-piece" />
                </FormField>
              </div>
              <FormField label="Libellé *">
                <input type="text" required value={formData.libelle}
                  onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                  placeholder="Vente facture …" data-testid="sage-form-libelle" autoFocus />
              </FormField>
              <div className="v3-form-row">
                <FormField label="Compte Débit">
                  <input type="text" value={formData.compteDebit}
                    onChange={(e) => setFormData({ ...formData, compteDebit: e.target.value })}
                    placeholder="411000" data-testid="sage-form-compte-debit" />
                </FormField>
                <FormField label="Compte Crédit">
                  <input type="text" value={formData.compteCredit}
                    onChange={(e) => setFormData({ ...formData, compteCredit: e.target.value })}
                    placeholder="707000" data-testid="sage-form-compte-credit" />
                </FormField>
              </div>
              <FormField label="Montant (FCFA) *">
                <input type="number" min="0" step="1" required value={formData.montant}
                  onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                  placeholder="0" data-testid="sage-form-montant" />
              </FormField>
              {formError && (
                <div className="v3-form-error">
                  <AlertTriangle size={14} /> {formError}
                </div>
              )}
            </div>
            <div className="v3-modal-foot">
              <button type="button" onClick={closeForm} className="v3-btn v3-btn-secondary" data-testid="sage-form-cancel">Annuler</button>
              <button type="submit" disabled={busy} className="v3-btn v3-btn-primary" data-testid="sage-form-submit">
                {busy ? 'Enregistrement…' : (editingId ? 'Mettre à jour' : 'Créer')}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmDelete && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
          <div className="v3-modal v3-modal-confirm">
            <div className="v3-modal-body" style={{ textAlign: 'center' }}>
              <AlertTriangle size={36} style={{ color: '#ef4444', margin: '0 auto 12px' }} />
              <h2 style={{ marginBottom: 6 }}>Confirmer la suppression</h2>
              <p style={{ color: '#94a3b8', fontSize: 13 }}>
                Supprimer l&apos;écriture <b>{confirmDelete.libelle}</b>
                {confirmDelete.montant ? <> · <b>{fmt(confirmDelete.montant)} FCFA</b></> : null}
                {' '}({confirmDelete.entity}) ?
              </p>
            </div>
            <div className="v3-modal-foot">
              <button type="button" onClick={() => setConfirmDelete(null)} className="v3-btn v3-btn-secondary">Annuler</button>
              <button type="button" onClick={doDelete} disabled={busy} className="v3-btn v3-btn-danger" data-testid="confirm-delete-sage-btn">
                {busy ? 'Suppression…' : 'Oui, supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`v3-toast v3-toast-${toast.type}`} data-testid="sage-toast">
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          <span>{toast.text}</span>
        </div>
      )}

      <style>{`
        .v3-modal-overlay { position: fixed; inset: 0; background: rgba(10,22,40,0.85); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
        .v3-modal { background: #0f1e3a; border: 1px solid rgba(214,243,47,0.15); border-radius: 18px; max-width: 540px; width: 100%; color: #e5e7eb; box-shadow: 0 30px 80px rgba(0,0,0,0.6); max-height: 90vh; display: flex; flex-direction: column; }
        .v3-modal-confirm { max-width: 420px; }
        .v3-modal-head { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid rgba(255,255,255,0.06); }
        .v3-modal-head h2 { font-size: 17px; font-weight: 800; color: #fff; letter-spacing: -0.3px; margin: 0; }
        .v3-modal-body { padding: 24px; overflow-y: auto; }
        .v3-modal-foot { display: flex; gap: 10px; justify-content: flex-end; padding: 16px 24px; border-top: 1px solid rgba(255,255,255,0.06); }
        .v3-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .v3-form-error { margin-top: 14px; padding: 12px 14px; background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); color: #fca5a5; font-size: 12px; border-radius: 8px; display: flex; align-items: center; gap: 8px; }
        .v3-form-field { margin-bottom: 16px; }
        .v3-form-field-label { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 1.2px; color: #71717a; font-weight: 700; margin-bottom: 6px; }
        .v3-form-field input, .v3-form-field select, .v3-form-field textarea { width: 100%; padding: 12px 14px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; color: #fff; font: 500 13px Inter; transition: all .2s; }
        .v3-form-field input:focus, .v3-form-field select:focus, .v3-form-field textarea:focus { outline: none; border-color: #D6F32F; background: rgba(0,0,0,0.4); box-shadow: 0 0 0 3px rgba(214,243,47,0.12); }
        .v3-toast { position: fixed; bottom: 24px; right: 24px; padding: 14px 20px; background: #0f1e3a; color: #fff; border-radius: 12px; display: inline-flex; align-items: center; gap: 10px; font: 600 13px Inter; z-index: 2000; box-shadow: 0 12px 32px rgba(0,0,0,0.5); animation: v3-toast-in .3s ease-out; }
        @keyframes v3-toast-in { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .v3-toast-success { border: 1px solid rgba(34,197,94,0.4); color: #86efac; }
        .v3-toast-error { border: 1px solid rgba(239,68,68,0.4); color: #fca5a5; }
        .v3-icon-btn { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #cbd5e1; padding: 6px 8px; border-radius: 8px; cursor: pointer; transition: all .2s; display: inline-flex; align-items: center; justify-content: center; }
        .v3-icon-btn:hover { background: rgba(255,255,255,0.1); color: #fff; }
        .v3-icon-btn-danger:hover { background: rgba(239,68,68,0.1); border-color: rgba(239,68,68,0.3); color: #fca5a5; }
        .v3-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      `}</style>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="v3-form-field">
      <label className="v3-form-field-label">{label}</label>
      {children}
    </div>
  );
}

function KpiCard({ icon, label, value, accent }) {
  return (
    <div className={`v3-kpi v3-kpi-${accent}`}>
      <div className="v3-kpi-icon">{icon}</div>
      <div className="v3-kpi-body">
        <div className="v3-kpi-label">{label}</div>
        <div className="v3-kpi-value">{value}</div>
      </div>
    </div>
  );
}
